import * as React from "react"
import { Link, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { PageProps, StudioProps } from "@/types/inertia"

// The guest email gate (M4, F5). Shown once the mock-up has been rendered and
// saved; on success the page downloads the locally rendered PNG straight
// away and the server emails a copy. Consent is unticked and never required.

type GateFields = {
  first_name: string
  email: string
  marketing_consent: boolean
  website: string
}

export function GateDialog({
  open,
  onOpenChange,
  mockupId,
  onUnlocked,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mockupId: number | null
  onUnlocked: (email: string) => void
}) {
  const form = useForm<GateFields>({
    first_name: "",
    email: "",
    marketing_consent: false,
    website: "",
  })
  const errors = form.errors as Partial<Record<keyof GateFields | "base", string>>

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (mockupId === null) return
    const email = form.data.email.trim()
    form.transform((data) => ({ ...data, marketing_consent: data.marketing_consent ? "1" : "0" }))
    form.post(`/mockups/${mockupId}/download`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: (page) => {
        const props = page.props as unknown as PageProps<StudioProps>
        if (props.download_unlocked) onUnlocked(email)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-modal" aria-describedby="gate-description">
        <DialogHeader>
          <div className="eyebrow">Download</div>
          <DialogTitle>Your mock-up is ready</DialogTitle>
          <DialogDescription id="gate-description">
            Tell us where to send a copy and your download starts straight away.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="studio-gate-form">
          {errors.base && (
            <div role="alert" className="field-error">
              {errors.base}
            </div>
          )}

          <div>
            <label htmlFor="gate-first-name" className="field-label">
              First name
            </label>
            <Input
              id="gate-first-name"
              autoComplete="given-name"
              required
              value={form.data.first_name}
              onChange={(e) => form.setData("first_name", e.target.value)}
              aria-invalid={Boolean(errors.first_name)}
              aria-describedby={errors.first_name ? "gate-first-name-error" : undefined}
              className="form-control-cellar"
            />
            {errors.first_name && (
              <div id="gate-first-name-error" className="field-error">
                {errors.first_name}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="gate-email" className="field-label">
              Email
            </label>
            <Input
              id="gate-email"
              type="email"
              autoComplete="email"
              required
              value={form.data.email}
              onChange={(e) => form.setData("email", e.target.value)}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "gate-email-error" : undefined}
              className="form-control-cellar"
            />
            {errors.email && (
              <div id="gate-email-error" className="field-error">
                {errors.email}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="gate-consent" className="studio-consent font-normal text-ink-body">
              <Checkbox
                id="gate-consent"
                checked={form.data.marketing_consent}
                onChange={(e) => form.setData("marketing_consent", e.target.checked)}
                aria-describedby="gate-consent-hint"
              />
              <span>Keep me posted about SOH GD Wines — occasional emails, unsubscribe any time</span>
            </label>
            <div id="gate-consent-hint" className="field-hint">
              Downloading works whether or not you tick the box. See our{" "}
              <Link href="/privacy">privacy policy</Link>.
            </div>
          </div>

          {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label htmlFor="gate-website">Website</label>
            <input
              id="gate-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.data.website}
              onChange={(e) => form.setData("website", e.target.value)}
            />
          </div>

          <Button type="submit" variant="gold" size="lg" disabled={form.processing || mockupId === null}>
            {form.processing ? "Preparing…" : "Send me my mock-up"}
          </Button>
          <div className="field-hint">Every mock-up is subject to graphic design approval by our team.</div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
